import React, {useContext} from "react";
import {QueryBuilderContext, QueryBuilderContextType} from "../QueryBuilderContext";
import {useAppSelector} from "../../../hooks";
import {selectValues} from "../../../reducers/settings";
import styles from "./DatasetList.module.scss";
import moment from "moment";
import {SwapRightOutlined} from "@ant-design/icons";
import {Spin} from "antd";
import _ from "lodash";

const format = (value: string) => {
    return moment(value, 'YYYY/MM').format('MMM YYYY')
}

interface RangeProp {
    name: string
    value: string
}

const Range = ({name, value}: RangeProp) => {
    const arr = value.split(',')
    return <div data-testid={name} className={styles.dateRange}>
        <span>{format(arr[0])}</span>
        <SwapRightOutlined/>
        <span>{format(arr[1])}</span>
    </div>
}

const RangeList = ({list}: { list: RangeProp[] }) => {

    const formatText = (i: RangeProp): string => {
        const text = i.name.substr(i.name.lastIndexOf('_') + 1)
        if (text.length <= 2) {
            return text
        } else {
            return _.startCase(text.toLowerCase())
        }
    }

    return <div className={styles.dateRangeList}>
        {
            list
                .map(i => {
                    const country = formatText(i) + ': '
                    const arr = i.value.split(',')
                    return <div data-testid={i.name} key={i.name}>
                        <div>
                            {country}
                            <span>
                                <span>{format(arr[0])} </span>
                                <SwapRightOutlined/>
                                <span> {format(arr[1])}</span>
                            </span>
                        </div>
                    </div>
                })
        }
    </div>
}

export const dateRangeValues = (dataset: string, settings: any): undefined | string | { name: string; value: any }[] => {
    const name = 'DATA_RANGE_' + dataset
    const value = (settings[name] as string)
    if (value) {
        return value
    } else {
        const list = Object
            .keys(settings)
            .filter(i => i.startsWith(`DATA_RANGE_${dataset}_`))
            .map(i => {
                return {name: i, value: settings[i]}
            })
        return _.isEmpty(list) ? undefined : list
    }
}

export const DateRange = () => {
    const {dataset} = useContext(QueryBuilderContext) as QueryBuilderContextType
    const settings = useAppSelector(selectValues)
    if (settings) {
        const name = 'DATA_RANGE_' + dataset
        const value = dateRangeValues(dataset, settings)
        if (_.isString(value)) {
            return <Range name={name} value={value}/>
        } else if (_.isArray(value)) {
            return <RangeList list={value}/>
        }
    }

    return <Spin className={styles.loading}/>
}

const cleanNamePrefix = (name: string) => {
    return name.replace(/DATA_RANGE_.*_/, "");
}

export const GenerateDatasetPeriodLabel = () => {

    const {dataset} = useContext(QueryBuilderContext) as QueryBuilderContextType
    const settings = useAppSelector(selectValues)

    if (!settings) {
        return <>Loading data range...</>
    }

    let datasetPeriods = dateRangeValues(dataset, settings)

    const formatDateString = (val: string) => val.replace(',', ' to ');

    let formattedDateRange = '';

    if (typeof datasetPeriods === 'string') {
        formattedDateRange = formatDateString(datasetPeriods)
    } else if (Array.isArray(datasetPeriods)) {
        formattedDateRange =
            datasetPeriods.map(item => {
                const name = cleanNamePrefix(item.name.toString())
                const range = item.value ? formatDateString(item.value) : item.toString()
                return name + ": " + range
            }).join(',')
    }

    if (!formattedDateRange) return null

    return (

        <div data-testid='date-range-text'>
            Valid period range(s) for your query dataset: {formattedDateRange}
        </div>
    )

}

export const CalculateEarliestYearOfDatasetPeriod = (): number => {

    const { dataset } = useContext(QueryBuilderContext) as QueryBuilderContextType;
    const settings = useAppSelector(selectValues);

    if (settings) {
        const value = dateRangeValues(dataset, settings);

        if (_.isString(value)) {
            // Single range string, parse to find the minimum
            const [start] = value.split(',').map(str => parseInt(str.trim(), 10));
            return isNaN(start) ? 0 : start; // Return parsed value or 0
        } else if (_.isArray(value)) {
            // List of ranges, compute the overall minimum
            const minimums = value
                .map(item => {
                    const [start] = item.value.split(',').map((str: string) => parseInt(str.trim(), 10));
                    return isNaN(start) ? null : start;
                })
                .filter(num => num !== null) as number[];

            // Return the minimum of the list or 0 if the list is empty
            return minimums.length > 0 ? Math.min(...minimums) : 0;
        }
    }

    return 0; // Default value if no settings
};